import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { startLoading, stopLoading } from "../redux/store/actions/actionsTypes";
import { useDispatch } from "react-redux";
import axios from "../api/axios";

const unSubscribeReasons = [
  "Too expensive",
  "Found a better alternative",
  "Not using it enough",
  "Bugs or technical issues",
  "Lack of features I need",
];
const formDataDefaultState = {
  reasons: [],
  suggestions: "",
  customMessage: null,
};
const UnsubscribeFlow = ({ subscriptionId, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = JSON.parse(localStorage.getItem("token"));
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(null);
  const [tempFormData, setTempFormData] = useState(formDataDefaultState);
  const [validCoupon, setValidCoupon] = useState(null);
  useEffect(() => {
    if (!subscriptionId) {
      toast.error("Invalid Subscription ID");
      navigate("/");
    }
    fetchSubscription();
  }, []);

  const handleQuestionnaireSubmit = (e) => {
    e.preventDefault();
    if (tempFormData == formDataDefaultState) {
      toast.info("Please share a valid feedback!");
      return;
    }
    setFormData(tempFormData);
    setTempFormData(formDataDefaultState);
    setStep(3);
  };

  const handleAcceptOffer = async () => {
    if (!validCoupon.coupon) {
      toast.error("Coupon not found!!!");
      return;
    }
    dispatch(startLoading());
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        "/add-discount",
        {
          subscriptionId,
          formData: formData ? { ...formData, retained: true } : null,
          coupon: validCoupon?.coupon,
        },
        config
      );
      if (response?.data && response.data?.status) {
        toast.info(
          "Great! You've got 20% off on your subscription on next purchase!"
        );
      } else {
        toast.error(response.data.message || "Failed to add discount");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      dispatch(stopLoading());
      setValidCoupon(null);
      onClose();
      navigate("/plan-history");
    }
  };

  const handleDeclineOffer = async () => {
    if (!subscriptionId) {
      toast.error("Invalid subscription ID");
      return;
    }

    dispatch(startLoading());
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        "/subscriptions/unsubscribe",
        {
          subscriptionId,
          formData: formData ? { ...formData, retained: true } : null,
        },
        config
      );
      if (response?.data && response.data?.status) {
        toast.success("Unsubscribed successfully!!!");
      } else {
        toast.error(response.data.message || "Failed to unsubscribe");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      dispatch(stopLoading());
      setValidCoupon(null);
      onClose();
      navigate("/plan-history");
    }
  };

  const fetchSubscription = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        "/subscriptions/get-coupons",
        {
          subscriptionId,
        },
        config
      );
      if (response && response?.data && response.data?.data) {
        setValidCoupon(response.data.data);
      }
    } catch (error) {
      console.log("Error in fetching the valid coupon: ", error);
    }
  };

  return (
    <div className="d-flex">
      <div className="unsubscribe-inner">
        <a className="back-home" href="/homepage">
          <i className="fa-solid fa-circle-arrow-left"></i> Back to Home
        </a>
        {step === 1 && (
          <div className="unsubscribe-form-container">
            <span style={{ fontSize: "22px", marginBottom: "5px" }}>
              Are you really sure you don't need to find any more winning
              products?
            </span>
            <div className="d-flex justify-around">
              <button className="greenColor-btn" onClick={() => setStep(2)}>
                Yes, I'm sure
              </button>
              <button
                className="greenColor-btn"
                onClick={() => {
                  toast.info("Thank You for staying with us!!!");
                  onClose();
                  navigate("/plan-history");
                }}
              >
                No, keep subscription
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <form
            onSubmit={handleQuestionnaireSubmit}
            className="unsubscribe-form"
          >
            <span className="unsubscribe-form-heading">
              Before you go, please tell us why:
            </span>
            <label>Why are you unsubscribing?</label>
            {unSubscribeReasons.map((reason, index) => (
              <div className="unsubscribe-form-options" key={index}>
                <input
                  type="checkbox"
                  id={`reason-${index}`}
                  name="reasons"
                  value={reason}
                  checked={tempFormData.reasons.includes(reason)}
                  onChange={(e) => {
                    const { value, checked } = e.target;
                    setTempFormData((prev) => ({
                      ...prev,
                      reasons: checked
                        ? [...prev.reasons, value]
                        : prev.reasons.filter((r) => r !== value),
                    }));
                  }}
                />
                <label htmlFor={`reason-${index}`}>{reason}</label>
              </div>
            ))}

            <div className="unsubscribe-form-options">
              <input
                type="checkbox"
                id="reason-other"
                name="otherReason"
                checked={tempFormData.customMessage !== null}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTempFormData((prev) => ({
                    ...prev,
                    customMessage: checked ? "" : null,
                  }));
                }}
              />
              <label htmlFor="reason-other">Other</label>
            </div>

            {tempFormData.customMessage !== null && (
              <textarea
                name="customMessage"
                className="unsubscribe-form-textarea"
                placeholder="Please tell us your reason"
                value={tempFormData.customMessage}
                onChange={(e) => {
                  setTempFormData((prev) => ({
                    ...prev,
                    customMessage: e.target.value,
                  }));
                }}
                required
              />
            )}

            <label htmlFor="suggestions">Any suggestions for us?</label>
            <textarea
              name="suggestions"
              className="unsubscribe-form-textarea"
              placeholder="We'd love to hear how we can improve"
              value={tempFormData.suggestions}
              onChange={(e) =>
                setTempFormData((prev) => ({
                  ...prev,
                  suggestions: e.target.value,
                }))
              }
            />

            <div className="d-flex justify-around">
              <button type="submit" className="greenColor-btn">
                Submit and Unsubscribe
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="greenColor-btn"
              >
                Skip and Unsubscribe
              </button>
            </div>
          </form>
        )}

        {step === 3 &&
          (validCoupon ? (
            <div className="unsubscribe-form-container">
              <span className="unsubscribe-form-heading">
                {validCoupon?.message ||
                  "No discount coupon description found!"}
              </span>
              <b>
                Note: Coupon will be applied automatically to next purchases.
              </b>
              <div className="d-flex justify-around">
                <button className="greenColor-btn" onClick={handleAcceptOffer}>
                  Yes, I’ll stay
                </button>
                <button className="greenColor-btn" onClick={handleDeclineOffer}>
                  No thanks
                </button>
              </div>
            </div>
          ) : (
            <div className="unsubscribe-form-container">
              <span className="unsubscribe-form-heading">
                Sorry to see you go, but we'll miss you
              </span>
              <div className="d-flex justify-around">
                <button className="greenColor-btn" onClick={handleDeclineOffer}>
                  Unsubscribe Now
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default UnsubscribeFlow;
